import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, fetchApiRef, configApiRef } from '@backstage/core-plugin-api';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  Divider,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import ScheduleIcon from '@material-ui/icons/Schedule';
import CancelIcon from '@material-ui/icons/Cancel';
import MergeTypeIcon from '@material-ui/icons/MergeType';
import SyncIcon from '@material-ui/icons/Sync';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import RefreshIcon from '@material-ui/icons/Refresh';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import WarningIcon from '@material-ui/icons/Warning';

/* ─── Types ─── */

interface PipelineSummary {
  id: number;
  iid: number;
  status: string;
  ref: string;
  sha: string;
  created_at: string;
  web_url: string;
}

interface MrSummary {
  iid: number;
  title: string;
  state: string;
  source_branch: string;
  target_branch: string;
  web_url: string;
  has_conflicts: boolean;
  draft: boolean;
}

interface ArgoAppStatus {
  health: string;
  sync: string;
  appName: string;
}

/* ─── Styles ─── */

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 0),
  },
  label: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    minWidth: 100,
  },
  value: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  mono: {
    fontFamily: 'monospace',
    fontSize: '0.78rem',
  },
  section: {
    padding: theme.spacing(1, 0),
  },
  sectionTitle: {
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    color: theme.palette.text.hint,
    letterSpacing: '0.05em',
    marginBottom: theme.spacing(0.5),
  },
}));

/* ─── Helpers ─── */

const GITLAB_PROXY = '/api/proxy/gitlab/api/v4';
const ARGOCD_PROXY = '/api/proxy/argocd/api';

function statusIcon(status: string, size = 16) {
  switch (status) {
    case 'success': case 'Healthy': case 'Synced':
      return <CheckCircleIcon style={{ fontSize: size, color: '#4caf50' }} />;
    case 'failed': case 'Degraded':
      return <ErrorIcon style={{ fontSize: size, color: '#f44336' }} />;
    case 'running': case 'Progressing':
      return <PlayArrowIcon style={{ fontSize: size, color: '#2196f3' }} />;
    case 'pending': case 'waiting_for_resource': case 'Suspended':
      return <ScheduleIcon style={{ fontSize: size, color: '#ff9800' }} />;
    case 'canceled':
      return <CancelIcon style={{ fontSize: size, color: '#757575' }} />;
    case 'OutOfSync':
      return <WarningIcon style={{ fontSize: size, color: '#ff9800' }} />;
    case 'Missing':
      return <ErrorIcon style={{ fontSize: size, color: '#f44336' }} />;
    default:
      return <FiberManualRecordIcon style={{ fontSize: size, color: '#bdbdbd' }} />;
  }
}

function statusColor(status: string): string {
  switch (status) {
    case 'success': case 'Healthy': case 'Synced': return '#4caf50';
    case 'failed': case 'Degraded': case 'Missing': return '#f44336';
    case 'running': case 'Progressing': return '#2196f3';
    case 'pending': case 'OutOfSync': case 'Suspended': return '#ff9800';
    case 'canceled': return '#757575';
    default: return '#9e9e9e';
  }
}

function statusLabelKo(status: string): string {
  switch (status) {
    case 'success': return '성공';
    case 'failed': return '실패';
    case 'running': return '실행 중';
    case 'pending': return '대기';
    case 'canceled': return '취소';
    case 'Healthy': return '정상';
    case 'Degraded': return '비정상';
    case 'Progressing': return '진행 중';
    case 'Suspended': return '중지';
    case 'Missing': return '없음';
    case 'Synced': return '동기화됨';
    case 'OutOfSync': return '미동기화';
    default: return status || '-';
  }
}

function formatTime(isoStr: string): string {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  const now = new Date();
  const diffMin = Math.floor((now.getTime() - d.getTime()) / 60000);
  if (diffMin < 1) return '방금';
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}시간 전`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return `${diffDay}일 전`;
  return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
}

/* ─── Component ─── */

export const GitlabOverviewCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const fetchApi = useApi(fetchApiRef);
  const configApi = useApi(configApiRef);

  const slug = entity?.metadata?.annotations?.['gitlab.com/project-slug'] ?? '';
  const argoAppName = entity?.metadata?.annotations?.['argocd/app-name'] ?? '';
  const gitlabBaseUrl =
    configApi.getOptionalConfigArray('integrations.gitlab')?.[0]
      ?.getOptionalString('baseUrl') ?? '';

  const [latestPipeline, setLatestPipeline] = React.useState<PipelineSummary | null>(null);
  const [openMrs, setOpenMrs] = React.useState<MrSummary[]>([]);
  const [argoStatus, setArgoStatus] = React.useState<ArgoAppStatus | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const fetchAll = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const promises: Promise<void>[] = [];

      if (slug) {
        const encodedSlug = encodeURIComponent(slug);

        // Latest pipeline
        promises.push(
          fetchApi.fetch(`${GITLAB_PROXY}/projects/${encodedSlug}/pipelines?per_page=1&order_by=id&sort=desc`)
            .then(async r => {
              if (!r.ok) throw new Error(`Pipeline: ${r.status}`);
              const data = await r.json();
              setLatestPipeline(data?.[0] ?? null);
            }),
        );

        // Open MRs
        promises.push(
          fetchApi.fetch(`${GITLAB_PROXY}/projects/${encodedSlug}/merge_requests?state=opened&per_page=5&order_by=updated_at`)
            .then(async r => {
              if (!r.ok) throw new Error(`MR: ${r.status}`);
              const data = await r.json();
              setOpenMrs(data ?? []);
            }),
        );
      }

      // ArgoCD status
      if (argoAppName) {
        promises.push(
          fetchApi.fetch(`${ARGOCD_PROXY}/v1/applications/${argoAppName}`)
            .then(async r => {
              if (!r.ok) {
                setArgoStatus(null);
                return;
              }
              const data = await r.json();
              setArgoStatus({
                health: data?.status?.health?.status ?? 'Unknown',
                sync: data?.status?.sync?.status ?? 'Unknown',
                appName: argoAppName,
              });
            })
            .catch(() => setArgoStatus(null)),
        );
      }

      await Promise.all(promises);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [slug, argoAppName, fetchApi]);

  React.useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-refresh 60s
  React.useEffect(() => {
    const interval = setInterval(fetchAll, 60000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  if (!slug && !argoAppName) return null;

  return (
    <Card variant="outlined">
      <CardHeader
        title="서비스 상태"
        titleTypographyProps={{ variant: 'subtitle1' }}
        action={
          <Tooltip title="새로고침">
            <IconButton size="small" onClick={fetchAll} disabled={loading}>
              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        }
      />
      <CardContent style={{ paddingTop: 0 }}>
        {error && (
          <Typography color="error" variant="body2" gutterBottom>{error}</Typography>
        )}

        {/* ─── Latest Pipeline ─── */}
        {slug && (
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>최근 파이프라인</Typography>
            {latestPipeline ? (
              <>
                <Box className={classes.row}>
                  <Box className={classes.value}>
                    {statusIcon(latestPipeline.status)}
                    <Chip
                      label={statusLabelKo(latestPipeline.status)}
                      size="small"
                      style={{
                        backgroundColor: statusColor(latestPipeline.status),
                        color: '#fff',
                        fontWeight: 600,
                        height: 20,
                      }}
                    />
                    <Typography className={classes.mono}>
                      #{latestPipeline.iid || latestPipeline.id}
                    </Typography>
                  </Box>
                  <Box className={classes.value}>
                    <Chip label={latestPipeline.ref} size="small" variant="outlined" style={{ height: 20, fontFamily: 'monospace', fontSize: '0.7rem' }} />
                    <Typography variant="caption" color="textSecondary">
                      {formatTime(latestPipeline.created_at)}
                    </Typography>
                    <Tooltip title="GitLab에서 보기">
                      <IconButton size="small" onClick={() => window.open(latestPipeline.web_url, '_blank')}>
                        <OpenInNewIcon style={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">파이프라인 없음</Typography>
            )}
          </Box>
        )}

        {slug && (argoAppName || openMrs.length > 0) && <Divider />}

        {/* ─── Open MRs ─── */}
        {slug && (
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              Open Merge Requests ({openMrs.length})
            </Typography>
            {openMrs.length === 0 ? (
              <Typography variant="body2" color="textSecondary">열린 MR 없음</Typography>
            ) : (
              openMrs.map(mr => (
                <Box key={mr.iid} className={classes.row}>
                  <Box className={classes.value}>
                    <MergeTypeIcon style={{ fontSize: 16, color: '#2196f3' }} />
                    <Typography variant="body2" style={{ fontSize: '0.8rem', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      !{mr.iid} {mr.title}
                    </Typography>
                    {mr.has_conflicts && <Chip label="충돌" size="small" style={{ backgroundColor: '#f44336', color: '#fff', height: 18, fontSize: '0.65rem' }} />}
                    {mr.draft && <Chip label="Draft" size="small" variant="outlined" style={{ height: 18, fontSize: '0.65rem' }} />}
                  </Box>
                  <Box className={classes.value}>
                    <Typography className={classes.mono} style={{ fontSize: '0.7rem', color: '#888' }}>
                      {mr.source_branch}
                    </Typography>
                    <Tooltip title="GitLab에서 보기">
                      <IconButton size="small" onClick={() => window.open(mr.web_url, '_blank')}>
                        <OpenInNewIcon style={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))
            )}
            {gitlabBaseUrl && slug && openMrs.length > 0 && (
              <Box mt={0.5}>
                <Typography
                  variant="caption"
                  color="primary"
                  style={{ cursor: 'pointer' }}
                  onClick={() => window.open(`${gitlabBaseUrl}/${slug}/-/merge_requests`, '_blank')}
                >
                  전체 보기 →
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {argoAppName && <Divider />}

        {/* ─── ArgoCD Status ─── */}
        {argoAppName && (
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>ArgoCD 배포 상태</Typography>
            {argoStatus ? (
              <Box className={classes.row}>
                <Box className={classes.value}>
                  {statusIcon(argoStatus.health)}
                  <Chip
                    label={statusLabelKo(argoStatus.health)}
                    size="small"
                    style={{
                      backgroundColor: statusColor(argoStatus.health),
                      color: '#fff',
                      fontWeight: 600,
                      height: 20,
                    }}
                  />
                </Box>
                <Box className={classes.value}>
                  <SyncIcon style={{ fontSize: 16, color: statusColor(argoStatus.sync) }} />
                  <Chip
                    label={statusLabelKo(argoStatus.sync)}
                    size="small"
                    style={{
                      backgroundColor: statusColor(argoStatus.sync),
                      color: '#fff',
                      fontWeight: 600,
                      height: 20,
                    }}
                  />
                  <Typography className={classes.mono} style={{ fontSize: '0.75rem', color: '#888' }}>
                    {argoStatus.appName}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">ArgoCD 연결 없음</Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
